/**
 * Barrage structured scoring — verified numeric VP from the Polish core
 * procedures already in barrageManual. Printed bonus / objective card texts
 * remain component-dependent.
 */

export type BarrageScoringRecord = {
  id: string;
  gameId: 'barrage';
  moduleId: 'base';
  category: 'round' | 'end-game' | 'ongoing';
  element: string;
  timing: 'mid-game' | 'end-game' | 'both';
  scoring: string;
  conditions: string;
  exceptions: string;
  returnsMeeple: string;
  sourceStatus: 'verified' | 'component-dependent';
  verified: boolean;
  order: number;
  keywords: string[];
  exactPointValues: string[];
};

export const barrageScoringRecords: BarrageScoringRecord[] = [
  {
    id: 'barrage-energy-prize',
    gameId: 'barrage',
    moduleId: 'base',
    category: 'round',
    element: 'Premia energii (koniec rundy)',
    timing: 'mid-game',
    scoring:
      '1. miejsce: 6 VP; 2. miejsce: 2 VP. Remis na 1.: zsumuj 6+2=8, podziel między remisujących, zaokrąglij w górę; bez 2. miejsca. Remis na 2.: każdy remisujący 1 VP.',
    conditions: 'Tylko gracze z ≥1 energii. Po fazie przepływu.',
    exceptions: 'Gracz z 0 energii nie wchodzi do rywalizacji.',
    returnsMeeple: 'N/A — inżynierowie wracają przy przygotowaniu kolejnej rundy.',
    sourceStatus: 'verified',
    verified: true,
    order: 10,
    keywords: ['energia', 'premia', 'runda'],
    exactPointValues: ['6', '2', '8', '1'],
  },
  {
    id: 'barrage-energy-zero',
    gameId: 'barrage',
    moduleId: 'base',
    category: 'round',
    element: 'Tor energii — pole 0',
    timing: 'mid-game',
    scoring: '3 kredyty i −3 VP.',
    conditions: 'Znacznik na polu 0 toru energii przy rozliczeniu kredytów.',
    exceptions: '—',
    returnsMeeple: 'N/A',
    sourceStatus: 'verified',
    verified: true,
    order: 20,
    keywords: ['kredyty', '0'],
    exactPointValues: ['3'],
  },
  {
    id: 'barrage-round-bonus',
    gameId: 'barrage',
    moduleId: 'base',
    category: 'round',
    element: 'Kafel premii rundy',
    timing: 'mid-game',
    scoring:
      'Minimum 6 energii. W odpowiedniej lub dalszej sekcji toru — pełna nagroda z kafla; w każdej wcześniejszej obniż o 4 VP; premia nie może dać ujemnych VP. Wartość nagród — zgodnie z nadrukiem kafla.',
    conditions: 'Po rozliczeniu energii i kredytów.',
    exceptions: 'Tekst konkretnego kafla premii jest component-dependent.',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 30,
    keywords: ['premia rundy', '6'],
    exactPointValues: ['6', '4'],
  },
  {
    id: 'barrage-foreign-channel',
    gameId: 'barrage',
    moduleId: 'base',
    category: 'ongoing',
    element: 'Cudzy kanał przy produkcji',
    timing: 'mid-game',
    scoring: 'Właściciel kanału: +1 kredyt i +1 VP za każdą przeprowadzoną kroplę.',
    conditions: 'Legalna produkcja przez kanał przeciwnika.',
    exceptions: 'Woda za cudzą tamą nie jest legalnym źródłem produkcji.',
    returnsMeeple: 'N/A',
    sourceStatus: 'verified',
    verified: true,
    order: 40,
    keywords: ['kanał', 'produkcja'],
    exactPointValues: ['1'],
  },
  {
    id: 'barrage-end-objective',
    gameId: 'barrage',
    moduleId: 'base',
    category: 'end-game',
    element: 'Cel końcowy',
    timing: 'end-game',
    scoring:
      'Miejsca 1/2/3: 15/10/5 VP według warunku kafla celu. Remis: podziel odpowiednie progi i zaokrąglij w górę. Warunek kafla — nadruk.',
    conditions: 'Po punktowaniu rundy 5 (bez zwykłego przygotowania kolejnej rundy).',
    exceptions: '—',
    returnsMeeple: 'N/A',
    sourceStatus: 'component-dependent',
    verified: true,
    order: 100,
    keywords: ['cel', 'końcowy', '15'],
    exactPointValues: ['15', '10', '5'],
  },
  {
    id: 'barrage-end-resources',
    gameId: 'barrage',
    moduleId: 'base',
    category: 'end-game',
    element: 'Pozostałe zasoby',
    timing: 'end-game',
    scoring:
      '1 VP za każde pełne 5 dostępnych kredytów, koparek i betoniarek łącznie. Maszyny na kole nie liczą się.',
    conditions: 'Tylko dostępne zasoby.',
    exceptions: 'Nie licz maszyn zablokowanych na kole budowy.',
    returnsMeeple: 'N/A',
    sourceStatus: 'verified',
    verified: true,
    order: 110,
    keywords: ['zasoby', '5'],
    exactPointValues: ['1', '5'],
  },
  {
    id: 'barrage-end-water',
    gameId: 'barrage',
    moduleId: 'base',
    category: 'end-game',
    element: 'Woda za własnymi tamami',
    timing: 'end-game',
    scoring: '1 VP za każdą kroplę zatrzymaną za twoimi tamami.',
    conditions: 'Tylko własne tamy.',
    exceptions: 'Nie licz kropli za neutralnymi lub cudzymi tamami.',
    returnsMeeple: 'N/A',
    sourceStatus: 'verified',
    verified: true,
    order: 120,
    keywords: ['woda', 'tamy'],
    exactPointValues: ['1'],
  },
  {
    id: 'barrage-end-tie',
    gameId: 'barrage',
    moduleId: 'base',
    category: 'end-game',
    element: 'Remis VP',
    timing: 'end-game',
    scoring:
      'Wygrywa gracz, który wyprodukował więcej energii w rundzie 5.',
    conditions: 'Równa suma VP po scoringu końcowym.',
    exceptions: 'Nie cofaj się do całej partii — porównaj energię rundy 5.',
    returnsMeeple: 'N/A',
    sourceStatus: 'verified',
    verified: true,
    order: 130,
    keywords: ['remis', 'energia'],
    exactPointValues: ['5'],
  },
];

export function filterBarrageScoring(
  options: { timingFilter?: BarrageScoringRecord['timing'] | 'all' } = {}
): BarrageScoringRecord[] {
  const timing = options.timingFilter ?? 'all';
  return barrageScoringRecords
    .filter((r) => timing === 'all' || r.timing === timing)
    .sort((a, b) => a.order - b.order);
}

export function validateBarrageScoringTables(): string[] {
  const issues: string[] = [];
  const ids = new Set<string>();
  for (const r of barrageScoringRecords) {
    if (ids.has(r.id)) issues.push(`Duplicate barrage scoring id ${r.id}`);
    ids.add(r.id);
    const blob = `${r.scoring} ${r.conditions} ${r.exceptions}`;
    for (const v of r.exactPointValues) {
      if (!blob.includes(v)) {
        issues.push(`Barrage ${r.id} missing exact value ${v}`);
      }
    }
  }
  const end = filterBarrageScoring({ timingFilter: 'end-game' });
  if (!end.some((r) => r.id === 'barrage-end-objective')) {
    issues.push('Missing end objective scoring');
  }
  const mid = filterBarrageScoring({ timingFilter: 'mid-game' });
  if (!mid.some((r) => r.id === 'barrage-energy-prize')) {
    issues.push('Missing round energy prize');
  }
  return issues;
}
